import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Building2, Home as HomeIcon, Briefcase, CheckCircle2, Phone, Mail, MapPin,
  Calendar, MessageSquare, ChevronRight, Shield, Star, Users,
  ClipboardList, type LucideIcon
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { addLead, TipoImovel, ROUTE_PATHS } from '@/lib/index';
import { queueEmail } from '@/lib/airtable';

// Mapeamento de localidade/distrito para zona AT CONTERA
function mapLocalidadeToZona(localidade: string): string {
  const loc = localidade.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  if (/lisboa|amadora|sintra|cascais|oeiras|almada|setubal|setubal|montijo|palmela|barreiro|seixal|loures|odivelas|mafra|vila franca/.test(loc)) return 'Lisboa';
  if (/porto|gaia|matosinhos|gondomar|maia|valongo|paredes|penafiel|amarante|felgueiras|povoa|trofa/.test(loc)) return 'Porto';
  if (/coimbra|figueira|leiria|pombal|marinha grande/.test(loc)) return 'Coimbra';
  if (/braga|guimaraes|barcelos|esposende|famalicao|povoa de lanhoso|vizela/.test(loc)) return 'Braga';
  if (/setubal|palmela|barreiro|seixal|almada|moita|montijo/.test(loc)) return 'Setúbal';
  if (/faro|olhao|loule|tavira|albufeira|portimao|lagos|silves|algarve|vila real de santo antonio/.test(loc)) return 'Faro / Algarve';
  if (/aveiro|ilhavo|oliveira de azemeis|ovar|estarreja|vagos|murtosa/.test(loc)) return 'Aveiro';
  if (/evora|beja|moura|serpa|vidigueira|elvas|portalegre/.test(loc)) return 'Évora';
  if (/viseu|lamego|tondela|mangualde|nelas|penalva/.test(loc)) return 'Viseu';
  return 'Lisboa'; // fallback
}

const schema = z.object({
  nome: z.string().min(2, 'Nome obrigatório'),
  email: z.string().email('Email inválido'),
  telefone: z.string().min(9, 'Telefone inválido'),
  morada: z.string().min(5, 'Morada obrigatória'),
  localidade: z.string().min(2, 'Localidade obrigatória'),
  codigoPostal: z.string().regex(/^\d{4}-\d{3}$/, 'Formato: 0000-000'),
  tipoImovel: z.enum(['predio', 'moradia', 'escritorio'] as const),
  dataVisita: z.string().min(1, 'Selecione uma data'),
  notas: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

const tipoOptions: { value: TipoImovel; label: string; icon: LucideIcon; desc: string }[] = [
  { value: 'predio', label: 'Prédio', icon: Building2, desc: 'Apartamento / Condomínio' },
  { value: 'moradia', label: 'Moradia', icon: HomeIcon, desc: 'Casa unifamiliar' },
  { value: 'escritorio', label: 'Escritório', icon: Briefcase, desc: 'Espaço comercial / Empresa' },
];

const trustItems: { icon: LucideIcon; title: string; desc: string }[] = [
  { icon: Star, title: '66 Anos de Experiência', desc: 'Décadas de inovação e excelência no setor de videoporteiros e segurança.' },
  { icon: Shield, title: 'Garantia de 5 Anos AT CONTERA', desc: 'No final de cada obra a AT CONTERA emite uma Garantia Oficial de 5 Anos — dada por uma empresa com mais de 6 décadas no mercado.' },
  { icon: Users, title: 'Rede de Instaladores', desc: 'Profissionais Comelit certificados e de confiança em todo o território nacional.' },
  { icon: CheckCircle2, title: 'Serviço 100% Gratuito', desc: 'Não cobramos nada ao cliente por este serviço de intermediação.' },
];

const steps: { num: string; icon: LucideIcon; title: string; desc: string }[] = [
  { num: '01', icon: ClipboardList, title: 'Diz-nos o que precisa',        desc: 'Basta a morada do edifício e o tipo de videoporteiro actual. Simples e sem compromisso.' },
  { num: '02', icon: Users,         title: 'Recebe orçamentos gratuitos',  desc: 'Instaladores certificados Comelit da sua zona entram em contacto com orçamentos para a sua obra.' },
  { num: '03', icon: Shield,        title: 'Obra executada com Garantia',  desc: 'Escolhe o instalador certificado, a obra é executada, e fica com a Garantia Oficial de 5 Anos da AT CONTERA.' },
];

const TIPO_LABEL: Record<string, string> = {
  predio: 'Prédio / Apartamento',
  moradia: 'Moradia',
  escritorio: 'Escritório / Espaço Comercial',
};

export default function HomePage() {
  const [submitted, setSubmitted] = useState(false);
  const [leadCode, setLeadCode] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [slideIdx, setSlideIdx] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);

  const slides = [
    {
      img: 'https://images.unsplash.com/photo-1768096865523-ca23d8811107?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1600&q=80',
      frase: 'O seu prédio já conhece a Contera.',
      sub: 'Há 66 anos.',
    },
    {
      img: 'https://images.unsplash.com/photo-1758696654710-bd61aa76c8a7?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1600&q=80',
      frase: 'Instaladores Certificados Comelit.',
      sub: 'Selecionados e avaliados pela Contera, na sua zona.',
    },
    {
      img: 'https://images.unsplash.com/photo-1652641592410-190fb18e43f4?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1600&q=80',
      frase: 'Garantia Oficial de 5 Anos.',
      sub: 'Emitida pela AT CONTERA no final de cada obra.',
    },
  ];

  useEffect(() => {
    const t = setInterval(() => {
      setFadeIn(false);
      setTimeout(() => {
        setSlideIdx(i => (i + 1) % slides.length);
        setFadeIn(true);
      }, 500);
    }, 5000);
    return () => clearInterval(t);
  }, []);

  const slide = slides[slideIdx];

  const { register, handleSubmit, watch, setValue, formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { tipoImovel: 'moradia' },
  });

  const tipoSelecionado = watch('tipoImovel');
  const moradaWatch = watch('morada');
  const localidadeWatch = watch('localidade');
  const cpWatch = watch('codigoPostal');
  const mapsQuery = [moradaWatch, localidadeWatch, cpWatch, 'Portugal'].filter(Boolean).join(', ');
  const mapsEmbed = mapsQuery.length > 10
    ? `https://www.google.com/maps?q=${encodeURIComponent(mapsQuery)}&output=embed`
    : null;

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  const onSubmit = async (data: FormData) => {
    setSubmitError('');
    try {
      const lead = await addLead(data as Parameters<typeof addLead>[0]);
      const code = `CTR-${lead.id.slice(0, 8).toUpperCase()}`;

      // Emails em paralelo — erro de email não bloqueia o sucesso
      await Promise.allSettled([
        queueEmail({
          email: 'at@contera.pt',
          nome: 'AT CONTERA',
          assunto: `Novo pedido de instalação — ${code} — ${data.localidade}`,
          mensagem: [
            `NOVO PEDIDO DE INSTALAÇÃO — ${code}`,
            ``,
            `Nome:          ${data.nome}`,
            `Email:         ${data.email}`,
            `Telefone:      ${data.telefone}`,
            `Tipo:          ${TIPO_LABEL[data.tipoImovel] ?? data.tipoImovel}`,
            `Morada:        ${data.morada}, ${data.localidade} ${data.codigoPostal}`,
            `Visita:        ${data.dataVisita.split('-').reverse().join('/')}`,
            data.notas ? `Notas:         ${data.notas}` : '',
            ``,
            `Gerir pedido em: /dashboard`,
          ].filter(Boolean).join('\n'),
        }),
        data.email ? queueEmail({
          email: data.email,
          nome: data.nome,
          assunto: `AT CONTERA — Pedido recebido: ${code}`,
          mensagem: [
            `Olá ${data.nome},`,
            ``,
            `O seu pedido foi registado com sucesso!`,
            ``,
            `O seu código de referência: ${code}`,
            ``,
            `⚠️  GUARDE ESTA REFERÊNCIA — vai precisar dela para acompanhar a sua obra e activar a garantia.`,
            ``,
            `O que acontece a seguir:`,
            `A nossa equipa vai atribuir um Instalador Certificado Comelit da sua zona,`,
            `que entrará em contacto consigo directamente para agendar a visita.`,
            ``,
            `🛡️  GARANTIA DE 5 ANOS AT CONTERA`,
            `No final de cada obra, a AT CONTERA emite uma Garantia Oficial de 5 Anos.`,
            `Esta garantia é dada por uma empresa com mais de 6 décadas no mercado.`,
            `É a sua total segurança — guarde sempre a referência ${code}.`,
            ``,
            `Dados do pedido:`,
            `Morada: ${data.morada}, ${data.localidade}`,
            `Tipo: ${TIPO_LABEL[data.tipoImovel]}`,
            `Data preferida: ${data.dataVisita.split('-').reverse().join('/')}`,
            ``,
            `Qualquer dúvida contacte-nos:`,
            `📞 217 967 663 | ✉️ at@contera.pt`,
            ``,
            `AT CONTERA | Mais de 66 anos de experiência`,
          ].join('\n'),
        }) : Promise.resolve(),
      ]);

      setLeadCode(code);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      console.error('Erro ao submeter pedido:', e);
      setSubmitError('Ocorreu um erro ao registar o pedido. Por favor tente novamente ou contacte-nos em comercial@contera.pt');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/80 sticky top-0 z-10 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-sm">C</span>
            </div>
            <div>
              <span className="font-bold text-foreground text-base tracking-tight">AT CONTERA</span>
              <span className="text-muted-foreground text-xs block leading-none">66 anos · Videoporteiros</span>
            </div>
          </div>
          <nav className="flex items-center gap-1 sm:gap-2">
            <a href="tel:217967663" className="hidden sm:flex items-center gap-1.5 text-sm font-bold text-primary hover:text-primary/80 transition-colors">
              <Phone size={14} />
              217 967 663
            </a>
            <a href="mailto:at@contera.pt" className="hidden sm:flex items-center gap-1.5 text-sm font-bold text-primary hover:text-primary/80 transition-colors mr-2">
              <Mail size={14} />
              at@contera.pt
            </a>
            <Button asChild variant="ghost" size="sm" className="text-xs sm:text-sm">
              <Link to={ROUTE_PATHS.INSTALADOR}>Sou Instalador</Link>
            </Button>
            <Button asChild variant="outline" size="sm" className="text-xs sm:text-sm">
              <Link to={ROUTE_PATHS.DASHBOARD}>Dashboard</Link>
            </Button>
          </nav>
        </div>
      </header>

      <AnimatePresence mode="wait">
        {submitted ? (
          <motion.div
            key="success"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 35 }}
            className="max-w-lg mx-auto px-4 py-20 text-center"
          >
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 size={40} className="text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-3">Pedido Recebido!</h2>
            <p className="text-muted-foreground mb-6 leading-relaxed">
              O seu pedido foi registado com sucesso. A equipa entrará em contacto em breve através dos Instaladores Certificados Comelit da sua zona.
            </p>

            {/* Código CTR em destaque */}
            <div className="bg-primary rounded-2xl p-6 mb-5 text-center shadow-lg">
              <p className="text-primary-foreground/80 text-xs font-semibold uppercase tracking-wider mb-2">O seu código de cliente</p>
              <p className="font-mono font-extrabold text-3xl text-white tracking-widest mb-2">{leadCode}</p>
              <p className="text-primary-foreground/70 text-xs">⚠️ Guarde este código — precisa dele para activar a Garantia de 5 Anos.</p>
            </div>

            {/* CTA Área do Cliente */}
            <div className="bg-green-50 border border-green-200 rounded-2xl p-5 mb-6 text-left">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center shrink-0 mt-0.5">
                  <CheckCircle2 size={18} className="text-green-600" />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-green-900 text-sm mb-1">O que acontece a seguir?</p>
                  <p className="text-green-700 text-xs leading-relaxed">
                    A equipa AT CONTERA vai analisar o seu pedido e atribuir um Instalador Certificado Comelit da sua zona. O instalador entrará em contacto consigo directamente para agendar a visita técnica.
                  </p>
                </div>
              </div>
            </div>
            {/* Mini steps no sucesso */}
            <div className="text-left bg-card border border-border rounded-xl p-4 mb-6 space-y-3">
              {steps.map((s) => (
                <div key={s.num} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{s.num.replace('0','')}</span>
                  <div>
                    <p className="text-sm font-medium text-foreground">{s.title}</p>
                    <p className="text-xs text-muted-foreground">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mb-6">
               🛡️ Garantia 5 anos · Instaladores Certificados Comelit · Serviço 100% Gratuito
            </p>
            <Button variant="outline" onClick={() => setSubmitted(false)}>Fazer outro pedido</Button>
          </motion.div>
        ) : (
          <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-5xl mx-auto px-4 py-10">

            {/* Hero com slideshow */}
            <div className="relative w-full h-[420px] sm:h-[520px] rounded-2xl overflow-hidden mb-12 shadow-2xl">
              {/* Imagem de fundo com transição */}
              <div
                className="absolute inset-0 bg-cover bg-center transition-all duration-700"
                style={{
                  backgroundImage: `url(${slide.img})`,
                  opacity: fadeIn ? 1 : 0,
                }}
              />
              {/* Overlay escuro gradiente */}
              <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />

              {/* Conteúdo */}
              <div
                className="relative h-full flex flex-col items-center justify-center text-center px-6"
                style={{ opacity: fadeIn ? 1 : 0, transition: 'opacity 0.5s ease' }}
              >
                <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white text-xs font-semibold px-4 py-1.5 rounded-full mb-6">
                  <Shield size={12} />
                  Distribuidor Oficial Comelit · Portugal
                </div>
                <h1 className="text-3xl sm:text-5xl font-extrabold text-white mb-3 leading-tight drop-shadow-lg max-w-3xl">
                  {slide.frase}
                </h1>
                <p className="text-white/80 text-base sm:text-lg max-w-xl leading-relaxed mb-6">
                  {slide.sub}
                </p>
                <button
                  type="button"
                  onClick={() => document.getElementById('pedido')?.scrollIntoView({ behavior: 'smooth' })}
                  className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-bold text-sm px-6 py-3 rounded-xl transition-colors shadow-lg mb-4"
                >
                  <ClipboardList size={16} />
                  Pedir orçamentos gratuitos
                </button>
                <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5">
                  <span className="text-white/70 text-xs flex items-center gap-1"><Star size={11} />66 anos de experiência</span>
                  <span className="text-white/70 text-xs flex items-center gap-1"><CheckCircle2 size={11} />Instaladores Certificados Comelit</span>
                  <span className="text-white/70 text-xs flex items-center gap-1"><Shield size={11} />Garantia de 5 anos</span>
                  <span className="text-white/70 text-xs flex items-center gap-1"><Users size={11} />Pagamentos seguros</span>
                </div>

              </div>

              {/* Número de telefone no slide */}
              <div className="absolute top-4 right-4">
                <div className="flex flex-col gap-1.5">
                  <a href="tel:217967663" className="flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/30 text-white font-bold text-sm px-4 py-2 rounded-xl hover:bg-white/25 transition-colors">
                    <Phone size={14} />
                    217 967 663
                  </a>
                  <a href="mailto:at@contera.pt" className="flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/30 text-white font-semibold text-sm px-4 py-2 rounded-xl hover:bg-white/25 transition-colors">
                    <Mail size={14} />
                    at@contera.pt
                  </a>
                </div>
              </div>

              {/* Indicadores de slide */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {slides.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => { setSlideIdx(i); setFadeIn(true); }}
                    className={`w-2 h-2 rounded-full transition-all ${i === slideIdx ? 'bg-white w-6' : 'bg-white/40'}`}
                  />
                ))}
              </div>
            </div>

            {/* Como Funciona */}
            <div className="mb-8">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest text-center mb-5">Como Funciona</p>
              <div className="flex flex-col sm:flex-row items-stretch border border-border rounded-2xl overflow-hidden">
                {steps.map((s, i) => (
                  <div key={s.num} className={`flex-1 flex items-start gap-4 px-5 py-5 ${i > 0 ? 'border-t sm:border-t-0 sm:border-l border-border' : ''}`}>
                    <span className="text-3xl font-black text-primary/15 leading-none shrink-0 mt-0.5">{s.num}</span>
                    <div>
                      <p className="font-bold text-sm text-foreground mb-1">{s.title}</p>
                      <p className="text-xs text-muted-foreground leading-relaxed">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Porquê a AT CONTERA */}
            <div className="mb-10">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest text-center mb-5">Porquê a AT CONTERA</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { num: '1', text: '66 anos a fornecer segurança a edifícios em Portugal' },
                  { num: '2', text: 'Instaladores certificados Comelit, selecionados e avaliados por nós' },
                  { num: '3', text: 'Orçamentos comparáveis, sem o risco de cortes na qualidade' },
                  { num: '4', text: 'Garantia até 5 anos e pagamentos seguros através da AT CONTERA' },
                ].map(item => (
                  <div key={item.num} className="flex items-start gap-3 p-4 border border-border rounded-xl bg-card">
                    <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                      <span className="text-primary font-bold text-xs">{item.num}</span>
                    </div>
                    <p className="text-sm text-foreground leading-relaxed">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Form */}
            <div id="pedido" className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-sm">
              <div className="mb-6">
                <h2 className="text-2xl font-black text-foreground mb-1">Pedido de Instalação</h2>
                <p className="text-sm text-muted-foreground">Preencha os dados abaixo — é gratuito e sem compromisso.</p>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Contacto */}
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4 flex items-center gap-2">
                    <Phone size={14} />Dados de Contacto
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="nome">Nome completo *</Label>
                      <Input id="nome" placeholder="Ana Silva" {...register('nome')} />
                      {errors.nome && <p className="text-destructive text-xs">{errors.nome.message}</p>}
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="telefone">Telefone *</Label>
                      <Input id="telefone" placeholder="912 345 678" {...register('telefone')} />
                      {errors.telefone && <p className="text-destructive text-xs">{errors.telefone.message}</p>}
                    </div>
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input id="email" type="email" placeholder="ana@email.com" {...register('email')} />
                      {errors.email && <p className="text-destructive text-xs">{errors.email.message}</p>}
                    </div>
                  </div>
                </div>

                {/* Tipo */}
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4 flex items-center gap-2">
                    <HomeIcon size={14} />Tipo de Imóvel *
                  </h3>
                  <div className="grid grid-cols-3 gap-3">
                    {tipoOptions.map((opt) => {
                      const isActive = tipoSelecionado === opt.value;
                      return (
                        <button key={opt.value} type="button" onClick={() => setValue('tipoImovel', opt.value)}
                          className={`rounded-xl border-2 p-4 flex flex-col items-center gap-2 transition-all duration-200 cursor-pointer ${isActive ? 'border-primary bg-primary/8 text-primary' : 'border-border bg-background text-muted-foreground hover:border-primary/40 hover:bg-accent'}`}>
                          <opt.icon size={24} />
                          <span className="font-semibold text-sm">{opt.label}</span>
                          <span className="text-xs text-center leading-tight opacity-70">{opt.desc}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Localização */}
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4 flex items-center gap-2">
                    <MapPin size={14} />Localização
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label htmlFor="morada">Morada *</Label>
                      <Input id="morada" placeholder="Rua das Flores, 12, 2º Dto" {...register('morada')} />
                      {errors.morada && <p className="text-destructive text-xs">{errors.morada.message}</p>}
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="localidade">Localidade *</Label>
                      <Input id="localidade" placeholder="Lisboa" {...register('localidade')} />
                      {errors.localidade && <p className="text-destructive text-xs">{errors.localidade.message}</p>}
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="codigoPostal">Código Postal *</Label>
                      <Input id="codigoPostal" placeholder="1000-001" {...register('codigoPostal')} />
                      {errors.codigoPostal && <p className="text-destructive text-xs">{errors.codigoPostal.message}</p>}
                    </div>
                  </div>
                </div>

                {/* Mapa dinâmico */}
                {mapsEmbed && (
                  <div className="rounded-xl overflow-hidden border border-border h-48">
                    <iframe
                      key={mapsEmbed}
                      src={mapsEmbed}
                      width="100%" height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Localização da obra"
                    />
                  </div>
                )}

                {/* Visita */}
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4 flex items-center gap-2">
                    <Calendar size={14} />Visita Técnica
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="dataVisita">Data preferida *</Label>
                      <Input id="dataVisita" type="date" min={minDate} {...register('dataVisita')} />
                      {errors.dataVisita && <p className="text-destructive text-xs">{errors.dataVisita.message}</p>}
                    </div>
                  </div>
                </div>

                {/* Notas */}
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4 flex items-center gap-2">
                    <MessageSquare size={14} />Informações Adicionais
                  </h3>
                  <Textarea id="notas" placeholder="Ex: portão à direita, interfone avariado, horário preferido..." className="resize-none h-24" {...register('notas')} />
                </div>

                {submitError && (
                  <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 flex items-start gap-2">
                    <span className="shrink-0 mt-0.5">⚠️</span>
                    <span>{submitError}</span>
                  </div>
                )}
                <Button type="submit" disabled={isSubmitting} size="lg" className="w-full text-base font-semibold h-12">
                  {isSubmitting ? 'A enviar...' : 'Pedir Instalador Certificado'}
                  <ChevronRight size={18} className="ml-1" />
                </Button>

                <p className="text-xs text-muted-foreground text-center">
                  Ao submeter, será contactado por Instaladores Certificados Comelit da sua zona. Garantia de 5 anos incluída em todos os trabalhos.
                </p>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <footer className="border-t border-border mt-16 py-8 text-center text-xs text-muted-foreground">
        <p className="font-semibold text-foreground text-sm mb-1">AT CONTERA</p>
        <p className="mb-2">Um serviço Contera · 66 anos · Portugal</p>
        <div className="flex items-center justify-center gap-4 mb-3">
          <a href="mailto:at@contera.pt" className="hover:text-foreground transition-colors">at@contera.pt</a>
          <span>·</span>
          <a href="tel:217967663" className="hover:text-foreground transition-colors">217 967 663</a>
        </div>
        <div className="flex items-center justify-center gap-4">
          <a href="#" className="hover:text-foreground transition-colors">Política de Privacidade</a>
          <span>·</span>
          <a href="mailto:at@contera.pt" className="hover:text-foreground transition-colors">Contactos</a>
        </div>
        <p className="mt-4">© {new Date().getFullYear()} AT CONTERA · Todos os direitos reservados</p>
      </footer>
    </div>
  );
}
